import 'core-js';
import { LoadingSpinner } from '@beamery/lib-ds-components';
import styled, { createGlobalStyle } from 'styled-components';
import { theme } from '@beamery/lib-ds-theme';
import { StrictMode, Suspense } from 'react';
import type { ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { ReactI18nextProvider } from 'react-i18next-helpers';
import { DesignSystemProvider } from 'design-system-extended';
import { i18NextConfig } from './i18n';
import { EmbeddedErrorTracking } from './components/embedded-error-tracking';
import { CandidateFormEmbedded } from './components/candidate-form-embedded';
import { EmbeddedModal } from './components/embedded-modal';
import { MetricsProvider } from './components/metrics';

// This element is needed to be able to style the div that is injected by the DesignSystemProvider
const AppContainer = styled.div`
  width: 100%;
  min-height: 100%;
  word-break: break-word;

  // box-sizing - because design system global styles disabled when embedded
  * {
    box-sizing: border-box;
  }

  > div {
    min-height: 100%;
    display: flex;
    flex-flow: column;
    align-items: stretch;
  }
`;

const GlobalRecaptchaStyles = createGlobalStyle`
  .grecaptcha-badge { 
    visibility: hidden; 
  }
`;

const Wrapper = ({ children }: { children: ReactNode }) => (
  <StrictMode>
    <AppContainer>
      <DesignSystemProvider theme={theme}>
        <ReactI18nextProvider config={i18NextConfig}>
          <GlobalRecaptchaStyles />
          <Suspense fallback={<LoadingSpinner />}>
            <EmbeddedErrorTracking>{children}</EmbeddedErrorTracking>
          </Suspense>
        </ReactI18nextProvider>
      </DesignSystemProvider>
    </AppContainer>
  </StrictMode>
);

const createAttachmentElement = () => {
  const element = document.createElement('div');
  document.body.appendChild(element);
  return element;
};

const flowIdDataAttributeName = `data-beamery-convert-flow-id`;
const flowSlugDataAttributeName = `data-beamery-convert-flow-slug`;

const inlineAttachmentElement =
  document.querySelector<HTMLElement>(`[${flowIdDataAttributeName}]`) ||
  document.querySelector<HTMLElement>(`[${flowSlugDataAttributeName}]`);

const flowId = inlineAttachmentElement?.dataset.beameryConvertFlowId;
const companyId = inlineAttachmentElement?.dataset.beameryConvertCompanyId;
const flowSlug = inlineAttachmentElement?.dataset.beameryConvertFlowSlug;
const companySlug = inlineAttachmentElement?.dataset.beameryConvertCompanySlug;

if (inlineAttachmentElement) {
  const root = createRoot(inlineAttachmentElement);
  root.render(
    <Wrapper>
      <MetricsProvider isEmbedded embeddedType='inline'>
        <CandidateFormEmbedded
          formId={flowId}
          formSlug={flowSlug}
          companyId={companyId}
          companySlug={companySlug}
        />
      </MetricsProvider>
    </Wrapper>,
  );
} else {
  const root = createRoot(createAttachmentElement());
  root.render(
    <Wrapper>
      <MetricsProvider isEmbedded embeddedType='modal'>
        <EmbeddedModal />
      </MetricsProvider>
    </Wrapper>,
  );
}
